import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import {
  PDF_EXPORT_CONFIG,
  PDF_TABLE_STYLES,
  REPORT_FILTER_LABELS,
  REPORT_FIELD_LABEL_MAP,
  CHARACTER_LIMITS,
} from '../../shared/appConstants';
import './reportExport.css';

const SPRINT_EXPORT_CONSTANTS = {
  CHART_SCALE_BUFFER: 15,
  CHART_SCALE_TOLERANCE: 5,
  PAGE_BREAK_BUFFER: 20,
  A4_WIDTH_MM: 210,
  A4_HEIGHT_MM: 297,
  CENTER_TIMEOUT_MS: 150,
  RESIZE_TIMEOUT_MS: 200,
};

/**
 * Creates table config for category-based charts
 */
export const createCategoryTableConfig = ({ title, chartData, fieldKey, translate }) => {
  if (!chartData?.length) return null;

  const sortedData = [...chartData].sort((a, b) => {
    if (b.tickets !== a.tickets) return b.tickets - a.tickets;
    return (a.name || '').localeCompare(b.name || '');
  });

  return {
    title,
    headers: [
      { label: REPORT_FIELD_LABEL_MAP[fieldKey] || fieldKey, key: 'fieldValue' },
      { label: 'Ticket Count', key: 'fieldCount' },
    ],
    data: sortedData.map(category => ({
      fieldValue: category.name || '',
      fieldCount: category.tickets?.toString() || '0',
    })),
  };
};

/**
 * Creates table config for Daily Ticket Creation chart
 */
export const createDailyTicketCreationTableConfig = ({ title, ticketsCount, translate }) => {
  if (!ticketsCount?.length) return null;

  const sortedData = [...ticketsCount].sort((a, b) => new Date(a.date) - new Date(b.date));

  return {
    title,
    headers: [
      { label: 'Date', key: 'date' },
      { label: 'Tickets Created', key: 'count' },
    ],
    data: sortedData.map(item => ({
      date: new Date(item.date).toLocaleDateString(),
      count: item.count.toString(),
    })),
  };
};

/**
 * Calculates export width based on element width
 */
const calculateExportWidth = (elementWidth) => {
  return elementWidth < PDF_EXPORT_CONFIG.MIN_WIDTH_THRESHOLD
    ? PDF_EXPORT_CONFIG.STANDARD_WIDTH
    : elementWidth;
};


/**
 * Expands all collapsed components and hides interactive icons for export
 */
const expandAllComponents = (clone) => {
  const cards = clone.querySelectorAll('.MuiCard-root');
  cards.forEach(card => {
    // 1. Force card height to expanded state
    // ONLY force 500px on chart cards. Filter cards and tables should be auto.
    const isChart = card.hasAttribute('data-chart-type') || 
                  card.querySelector('.recharts-wrapper') || 
                  card.textContent.includes('vs Ticket Count');
    const isTable = card.classList.contains('report-table-card') || 
                   card.textContent.includes('Upcoming Due Dates');
    const isFilter = card.closest('.reports-filter-card') || 
                    card.querySelector('input') || 
                    card.textContent.includes('Project');

    if (isChart && !isTable && !isFilter) {
      card.style.height = '500px';
    } else {
      card.style.height = 'auto';
    }
    
    // 2. Find and force all Collapse containers to be visible
    const collapseContainers = card.querySelectorAll('.MuiCollapse-root, .MuiCollapse-vertical');
    collapseContainers.forEach(container => {
      container.style.display = 'block';
      container.style.height = 'auto';
      container.style.overflow = 'visible';
      container.style.visibility = 'visible';
      
      const wrapper = container.querySelector('.MuiCollapse-wrapper');
      if (wrapper) {
        wrapper.style.display = 'block';
        wrapper.style.height = 'auto';
        wrapper.style.visibility = 'visible';
      }
      
      const inner = container.querySelector('.MuiCollapse-wrapperInner');
      if (inner) {
        inner.style.display = 'block';
        inner.style.height = 'auto';
        inner.style.visibility = 'visible';
      }
    });

    // 3. Hide interactive icons (Expand/Collapse arrows, Pin icons)
    const interactiveIcons = card.querySelectorAll('button, .MuiIconButton-root, svg[data-testid*="ExpandMore"], svg[data-testid*="PushPin"]');
    interactiveIcons.forEach(icon => {
      icon.style.display = 'none';
      icon.style.visibility = 'hidden';
    });
  });
};

/**
 * Rearranges layout for export:
 * 1. Moves Task Type chart to be beside Assignee chart (replaces Calendar in that row)
 * 2. Moves Calendar chart to its own row with 100% width (replacing table card)
 */
const rearrangeExportLayout = (clone) => {
  // Expand all components first
  expandAllComponents(clone);
  
  // Specific fix for Sprint Management cards to ensure equal height in PDF
  const sprintChartsRow = clone.querySelector('.charts-row');
  if (sprintChartsRow) {
    const burndownCard = sprintChartsRow.querySelector('.burndown-card');
    const atRiskCard = sprintChartsRow.querySelector('.at-risk-card');
    const originalRow = document.querySelector('.sprint-management-main-container .charts-row');
    
    let isRow = false;
    
    if (originalRow) {
      // Sync the clone's layout perfectly with whatever the user's screen is currently showing
      isRow = window.getComputedStyle(originalRow).flexDirection === 'row';
      sprintChartsRow.style.setProperty('flex-direction', isRow ? 'row' : 'column', 'important');
      
      if (isRow) {
        // Enforce strict mathematical widths so the internal chart cannot "bully" the flex container into expanding
        if (sprintChartsRow.children[0]) {
          sprintChartsRow.children[0].style.setProperty('width', 'calc(60% - 12px)', 'important');
          sprintChartsRow.children[0].style.setProperty('flex', 'none', 'important');
        }
        if (sprintChartsRow.children[1]) {
          sprintChartsRow.children[1].style.setProperty('width', 'calc(40% - 12px)', 'important');
          sprintChartsRow.children[1].style.setProperty('flex', 'none', 'important');
        }
      } else {
        if (sprintChartsRow.children[0]) sprintChartsRow.children[0].style.setProperty('width', '100%', 'important');
        if (sprintChartsRow.children[1]) sprintChartsRow.children[1].style.setProperty('width', '100%', 'important');
      }
    }

    // Only force equal heights if they are actually in a row layout!
    if (burndownCard && atRiskCard && isRow) {
      const realBurndown = document.querySelector('.burndown-card');
      if (realBurndown) {
        const height = `${realBurndown.clientHeight}px`;
        atRiskCard.style.height = height;
        atRiskCard.style.minHeight = height;
        burndownCard.style.height = height;
        burndownCard.style.minHeight = height;
      }
    }
  }
};

/**
 * Creates date display element for export
 */
const createDateDisplay = (doc, input, inputContainer) => {
  const placeholderText = input.placeholder || 'mm/dd/yyyy';
  const dateDisplay = doc.createElement('div');
  dateDisplay.className = 'report-export-date-display';

  if (input.value) {
    const formattedDate = new Date(input.value + 'T00:00:00').toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    dateDisplay.textContent = formattedDate;
    dateDisplay.classList.add('report-export-date-display--value');
  } else {
    dateDisplay.textContent = placeholderText;
    dateDisplay.classList.add('report-export-date-display--placeholder');
  }

  input.style.display = 'none';
  inputContainer.classList.add('report-export-input-container');
  inputContainer.appendChild(dateDisplay);
};

/**
 * Processes date inputs in cloned document
 */
const processDateInputs = (clonedDoc) => {
  const dateInputs = clonedDoc.querySelectorAll('input[type="date"]');
  dateInputs.forEach((input) => {
    const inputContainer = input.closest('.MuiInputBase-root');
    if (inputContainer) {
      createDateDisplay(clonedDoc, input, inputContainer);
    }
  });
};

/**
 * Ensures chart labels are visible in cloned document
 */
const ensureChartLabelsVisible = (clonedDoc) => {
  const textElements = clonedDoc.querySelectorAll('text, tspan, [class*="recharts-label"], [class*="recharts-text"]');
  textElements.forEach((text) => {
    text.style.opacity = '1';
    text.style.visibility = 'visible';
    
    if (text.tagName === 'text' || text.tagName === 'tspan') {
      const fill = text.getAttribute('fill');
      if (!fill || fill === 'none' || fill === 'transparent') {
        text.setAttribute('fill', '#333');
      }
    }
  });
};

/**
 * Modifies pie chart legends for export: limits to 6 total items and adds +n indicator
 * Very defensive - only modifies when absolutely certain it's a pie chart legend with >6 items
 */
const modifyPieChartLegendsForExport = (exportContainer) => {
  if (!exportContainer) return;
  
  // Use scoped selector with data attributes and class names for better performance
  // Match both integer and decimal percentages: "12%" or "12.3%"
  const pctRegex = /\((\d+(\.\d+)?%)\)/;
  
  // Find legend containers using the class we added
  const legendContainers = exportContainer.querySelectorAll('.reports-pie-legend-container');
  
  // Group legend items by container
  const legendGroups = new Map();
  
  legendContainers.forEach(container => {
    // Find legend items using data attribute or class
    const legendItems = container.querySelectorAll('[data-legend-item="true"], .reports-pie-legend-item');
    
    if (legendItems.length === 0) return;
    
    // Find the inner container with rows
    const innerContainer = container.querySelector('.reports-pie-legend-inner');
    if (!innerContainer) return;
    
    // Find row containers
    const rows = innerContainer.querySelectorAll('.reports-pie-legend-row');
    if (rows.length !== 2) return;
    
    // Verify rows contain legend items with percentage pattern
    const row1Items = Array.from(rows[0].querySelectorAll('[data-legend-item="true"], .reports-pie-legend-item'))
      .filter(item => {
        const text = item.textContent || '';
        return pctRegex.test(text);
      });
    const row2Items = Array.from(rows[1].querySelectorAll('[data-legend-item="true"], .reports-pie-legend-item'))
      .filter(item => {
        const text = item.textContent || '';
        return pctRegex.test(text);
      });
    
    if (row1Items.length > 0 || row2Items.length > 0) {
      legendGroups.set(innerContainer, {
        parent: innerContainer,
        row1: rows[0],
        row2: rows[1],
        items: [...row1Items, ...row2Items]
      });
    }
  });
  
  // Process each legend group
  for (const [, group] of legendGroups.entries()) {
    const { row1, row2, items, parent } = group;
    const totalItems = items.length;
    
    // Skip status chart - check for data-chart-type="status" attribute
    const chartCard = parent.closest('[data-chart-type="status"]');
    if (chartCard) {
      continue;
    }
    
    // ONLY modify if there are MORE than 6 items
    if (totalItems > 6) {
      // Separate items by row
      const row1Items = items.filter(item => row1.contains(item));
      const row2Items = items.filter(item => row2.contains(item));
      
      const remainingCount = totalItems - 6;
      
      // Remove excess items from row 1 (keep only first 3)
      // Use Array.from to avoid live NodeList issues when removing nodes
      Array.from(row1Items).forEach((item, index) => {
        if (index >= 3) {
          item.remove();
        }
      });
      
      // Remove excess items from row 2 (keep only first 3)
      Array.from(row2Items).forEach((item, index) => {
        if (index >= 3) {
          item.remove();
        }
      });
      
      // Add +n indicator at the end of row 2
      if (remainingCount > 0) {
        // Remove any existing indicator first
        const existingIndicator = row2.querySelector('[data-export-indicator]');
        if (existingIndicator) {
          existingIndicator.remove();
        }
        
        // Create the +n indicator as a Chip element
        const chip = document.createElement('div');
        chip.setAttribute('data-export-indicator', 'true');
        chip.setAttribute('class', 'MuiChip-root MuiChip-sizeSmall reports-legend-export-indicator');
        
        const chipLabel = document.createElement('span');
        chipLabel.setAttribute('class', 'MuiChip-label');
        chipLabel.textContent = `+${remainingCount}`;
        chip.appendChild(chipLabel);
        
        row2.style.setProperty('display', 'flex', 'important');
        row2.style.setProperty('flex-wrap', 'nowrap', 'important');
        row2.appendChild(chip);
      }
      
      // Hide overflow on the parent container (legend container)
      const outerContainer = parent.parentElement;
      if (outerContainer && outerContainer.classList.contains('reports-pie-legend-container')) {
        outerContainer.classList.add('reports-legend-export-container');
      }
    }
  }
};

/**
 * Creates hidden clone of main element for export
 */
const createExportContainer = (mainElement) => {
  const elementWidth = mainElement.offsetWidth || mainElement.clientWidth;
  const exportWidth = calculateExportWidth(elementWidth);
  const mainClone = mainElement.cloneNode(true);

  // Apply export styles via class
  mainClone.classList.add('report-export-main-clone');
  mainClone.style.width = `${exportWidth}px`;
  mainClone.style.minWidth = `${exportWidth}px`;
  mainClone.style.maxWidth = `${exportWidth}px`;

  // Rearrange layout for export
  rearrangeExportLayout(mainClone);

  document.body.appendChild(mainClone);
  
  // Force all ResponsiveContainer elements to recalculate and center after layout changes
  requestAnimationFrame(() => {
    setTimeout(() => {
      const resizedCharts = mainClone.querySelectorAll('.report-export-chart-resized');
      
      // BATCH READS: collect all elements and their measurements first to avoid DOM thrashing
      const chartMeasurements = [];
      
      resizedCharts.forEach(card => {
        const containers = card.querySelectorAll('.recharts-responsive-container');
        containers.forEach(container => {
          const chartBox = container.closest('[class*="MuiBox-root"]');
          
          chartMeasurements.push({
            container,
            chartBox,
          });
        });
      });
      
      // BATCH WRITES (Phase 1): Apply 100% widths to force Recharts to stretch
      chartMeasurements.forEach(({ container, chartBox }) => {
        if (chartBox) {
          chartBox.style.width = '100%';
        }
        container.style.width = '100%';
        container.style.position = 'relative';
      });
      
      // Force a single reflow for all elements
      if (chartMeasurements.length > 0) {
        void chartMeasurements[0].container.offsetWidth;
      }
      
      // Wait for ResponsiveContainer to recalculate, then center
      setTimeout(() => {
        // BATCH READS (Phase 2): Get dimensions after Recharts resized
        const scalingUpdates = [];
        
        chartMeasurements.forEach(({ container }) => {
          const svg = container.querySelector('svg');
          const wrapper = container.querySelector('.recharts-wrapper');
          
          if (svg && wrapper) {
            const containerWidth = container.offsetWidth;
            const wrapperWidth = wrapper.offsetWidth || parseFloat(wrapper.getAttribute('width')) || 0;
            const wrapperHeight = wrapper.offsetHeight || parseFloat(wrapper.getAttribute('height')) || 0;
            
            scalingUpdates.push({
              container,
              wrapper,
              svg,
              containerWidth,
              wrapperWidth,
              wrapperHeight
            });
          }
        });
        
        // BATCH WRITES (Phase 2): Apply transforms and scaling
        scalingUpdates.forEach(({ container, wrapper, svg, containerWidth, wrapperWidth, wrapperHeight }) => {
          wrapper.style.margin = '0 auto';
          wrapper.style.display = 'block';
          svg.style.display = 'block';
          svg.style.margin = '0 auto';
          
          // Scale proportionally to perfectly fit the PDF container width!
          if (wrapperWidth > 0 && Math.abs(wrapperWidth - containerWidth) > SPRINT_EXPORT_CONSTANTS.CHART_SCALE_TOLERANCE) {
            // Add safety buffer to guarantee it never touches the boundary
            const safeContainerWidth = Math.max(containerWidth - SPRINT_EXPORT_CONSTANTS.CHART_SCALE_BUFFER, 1);
            const scale = safeContainerWidth / wrapperWidth;
            
            wrapper.style.margin = '0'; // Reset margin to align perfectly with top-left origin
            wrapper.style.transform = `scale(${scale})`;
            wrapper.style.transformOrigin = 'top left';
            // Adjust container height to prevent huge whitespace below scaled chart
            container.style.height = `${wrapperHeight * scale}px`;
          }
        });
      }, SPRINT_EXPORT_CONSTANTS.CENTER_TIMEOUT_MS);
      
      window.dispatchEvent(new Event('resize'));
    }, SPRINT_EXPORT_CONSTANTS.RESIZE_TIMEOUT_MS);
  });
  
  return mainClone;
};

/**
 * Removes export container from DOM
 */
const removeExportContainer = (exportContainer) => {
  exportContainer?.remove();
};

/**
 * Captures element content using html2canvas
 */
const captureElementContent = async (element, options = {}) => {
  const fixedWidth = element.offsetWidth || element.clientWidth;
  const scrollHeight = element.scrollHeight;

  const defaultOptions = {
    scale: PDF_EXPORT_CONFIG.CANVAS_SCALE,
    useCORS: true,
    logging: false,
    windowHeight: scrollHeight,
    height: scrollHeight,
    width: fixedWidth,
    scrollX: 0,
    scrollY: 0,
    allowTaint: true,
    windowWidth: fixedWidth,
  };

  return await html2canvas(element, { ...defaultOptions, ...options });
};

/**
 * Converts table config to autoTable format
 */
const convertTableDataToAutoTableFormat = (tableConfig) => {
  const { headers, data } = tableConfig;
  const head = [['S.No.', ...headers.map(h => h.label)]];
  const body = data.map((row, index) => [
    (index + 1).toString(),
    ...headers.map(header => String(row[header.key] || '')),
  ]);
  return { ...tableConfig, head, body };
};

/**
 * Calculates column widths for table
 */
const calculateColumnWidths = (columnCount) => {
  const availableWidth = PDF_EXPORT_CONFIG.PAGE_WIDTH - (PDF_EXPORT_CONFIG.MARGIN * 2);
  const dataColumnWidth = (availableWidth - PDF_EXPORT_CONFIG.SERIAL_NUMBER_COLUMN_WIDTH) / (columnCount - 1);
  
  const columnStyles = { 0: { cellWidth: PDF_EXPORT_CONFIG.SERIAL_NUMBER_COLUMN_WIDTH, halign: 'center' } };
  for (let i = 1; i < columnCount; i++) {
    columnStyles[i] = { cellWidth: dataColumnWidth };
  }
  return columnStyles;
};

/**
 * Draws table title with colored background
 */
const drawTableTitle = (pdf, title, filterType, startY) => {
  const boxWidth = PDF_EXPORT_CONFIG.PAGE_WIDTH - (PDF_EXPORT_CONFIG.MARGIN * 2);
  const boxX = PDF_EXPORT_CONFIG.MARGIN;
  const boxY = startY + PDF_EXPORT_CONFIG.TITLE_SPACING;

  pdf.setFillColor(59, 130, 246);
  pdf.rect(boxX, boxY, boxWidth, PDF_EXPORT_CONFIG.TITLE_BOX_HEIGHT, 'F');

  let titleText = title;
  if (filterType && REPORT_FILTER_LABELS[filterType]) {
    titleText = `${title} (${REPORT_FILTER_LABELS[filterType]})`;
  }

  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(255, 255, 255);
  
  const textWidth = pdf.getTextWidth(titleText);
  const textX = (PDF_EXPORT_CONFIG.PAGE_WIDTH - textWidth) / 2;
  pdf.text(titleText, textX, boxY + 8);
  
  pdf.setTextColor(0, 0, 0);
  return boxY + PDF_EXPORT_CONFIG.TITLE_BOX_HEIGHT + 8;
};

/**
 * Adds table to PDF using autoTable
 */
const addTableToPDF = (pdf, tableConfig, startY) => {
  const { head, body, title, filterType } = convertTableDataToAutoTableFormat(tableConfig);
  
  if (title) {
    startY = drawTableTitle(pdf, title, filterType, startY);
  }

  const columnStyles = calculateColumnWidths(head[0].length);

  pdf.autoTable({
    head,
    body,
    startY,
    theme: 'striped',
    tableWidth: 'wrap',
    headStyles: PDF_TABLE_STYLES.HEAD,
    bodyStyles: PDF_TABLE_STYLES.BODY,
    alternateRowStyles: PDF_TABLE_STYLES.ALTERNATE_ROW,
    styles: PDF_TABLE_STYLES.BASE,
    columnStyles,
    margin: { top: 5, right: PDF_EXPORT_CONFIG.MARGIN, bottom: 10, left: PDF_EXPORT_CONFIG.MARGIN },
    showHead: 'everyPage',
  });

  return pdf.lastAutoTable.finalY + PDF_EXPORT_CONFIG.TABLE_SPACING;
};

/**
 * Adds canvas to PDF with pagination
 */
const addCanvasToPDF = (canvas, pdf, options = {}) => {
  const { imgWidth = PDF_EXPORT_CONFIG.PAGE_WIDTH, pageHeight = PDF_EXPORT_CONFIG.PAGE_HEIGHT } = options;
  const imgData = canvas.toDataURL('image/png');
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  let heightLeft = imgHeight;
  let position = 0;

  pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
  heightLeft -= pageHeight;

  while (heightLeft > 0) {
    position = heightLeft - imgHeight;
    pdf.addPage();
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
  }
};

/**
 * Calculates starting Y position for tables
 */
const calculateTableStartY = (canvas) => {
  const imgHeight = (canvas.height * PDF_EXPORT_CONFIG.PAGE_WIDTH) / canvas.width;
  const remainder = imgHeight % PDF_EXPORT_CONFIG.PAGE_HEIGHT;
  
  if (remainder === 0 || remainder > 250) {
    return { startY: 20, needsNewPage: true };
  }
  return { startY: remainder + 10, needsNewPage: false };
};

/**
 * Registers autoTable plugin if needed
 */
const ensureAutoTablePlugin = async () => {
  const testPdf = new jsPDF();
  if (!testPdf.autoTable) {
    const autoTableModule = await import('jspdf-autotable');
    autoTableModule.applyPlugin?.(jsPDF);
  }
};

/**
 * Adjusts margins to prevent elements from breaking across PDF pages
 * Strictly scoped to Sprint Management to prevent side effects on other reports.
 */
const adjustPageBreaksForExport = (exportContainer) => {
  if (!exportContainer) return;
  
  // Only apply this logic to the Sprint Management dashboard
  if (!exportContainer.classList.contains('sprint-management-main-container')) {
    return;
  }
  
  try {
    const exportWidth = exportContainer.offsetWidth || exportContainer.clientWidth;
    if (!exportWidth) return;
    
    // PDF is A4: 210mm wide x 297mm high. Calculate equivalent page height in pixels.
    const domPageHeight = (SPRINT_EXPORT_CONSTANTS.A4_HEIGHT_MM * exportWidth) / SPRINT_EXPORT_CONSTANTS.A4_WIDTH_MM;
    
    const unbreakableSelectors = [
      '.sprint-health-container', 
      '.sprint-load-row', 
      '.charts-row', 
      '.summary-section',
      '.sm-card',
      '.report-table-card'
    ].join(', ');
    
    const elements = Array.from(exportContainer.querySelectorAll(unbreakableSelectors));
    elements.forEach(el => {
      // READS must be interleaved because modifying margin of a previous element
      // physically pushes all subsequent elements down. Batching reads causes us to
      // calculate page boundaries based on stale, pre-pushed positions!
      const containerRect = exportContainer.getBoundingClientRect();
      const rect = el.getBoundingClientRect();
      
      const topRelative = rect.top - containerRect.top;
      const bottomRelative = rect.bottom - containerRect.top;
      
      // Skip if element is taller than a page or if dimensions are invalid
      if (rect.height >= domPageHeight || rect.height === 0) return;
      
      // Calculate which page the top and bottom of the element fall on
      const startPage = Math.floor((topRelative + 5) / domPageHeight);
      const endPage = Math.floor((bottomRelative - 5) / domPageHeight);
      
      if (endPage > startPage) {
        const currentMargin = parseFloat(window.getComputedStyle(el).marginTop) || 0;
        const pushAmount = (endPage * domPageHeight) - topRelative + SPRINT_EXPORT_CONSTANTS.PAGE_BREAK_BUFFER;
        
        // WRITE
        el.style.marginTop = `${currentMargin + pushAmount}px`;
      }
    });
    
  } catch (err) {
    console.warn("Failed to adjust page breaks for PDF export:", err);
  }
};

/**
 * Main export function
 */
export const exportReportsDashboardToPDF = async ({
  mainSelector = '.main',
  tables = [],
  filename = `reports-dashboard-${new Date().toISOString().split('T')[0]}.pdf`,
}) => {
  await ensureAutoTablePlugin();

  const mainElement = document.querySelector(mainSelector);
  if (!mainElement) {
    throw new Error(`Element with selector "${mainSelector}" not found`);
  }

  const exportContainer = createExportContainer(mainElement);

  try {
    await new Promise(resolve => setTimeout(resolve, PDF_EXPORT_CONFIG.RENDER_DELAY));

    // Execute page break adjustments on the clone BEFORE captureElementContent!
    // This is critical because adjustPageBreaksForExport adds margins which increases total height.
    // If done inside onclone, html2canvas will use the unadjusted scrollHeight and cut off the bottom!
    // exportContainer is ALREADY a clone detached from the live DOM view (created by createExportContainer).
    adjustPageBreaksForExport(exportContainer);

    const pdf = new jsPDF('p', 'mm', 'a4');
    const canvas = await captureElementContent(exportContainer, {
      onclone: (clonedDoc) => {
        const clonedMain = clonedDoc.querySelector('.report-export-main-clone');
        if (clonedMain) {
          clonedMain.style.maxWidth = clonedMain.style.width;
        }
        ensureChartLabelsVisible(clonedDoc);
        processDateInputs(clonedDoc);
        
        // Modify pie chart legends in the cloned document - must be done synchronously in onclone
        modifyPieChartLegendsForExport(clonedMain || clonedDoc);
        
        // Fix truncated text to manually add ellipsis in PDF
        // html2canvas doesn't render CSS text-overflow: ellipsis properly, so we manually add "..."
        // Exclude Date Range, Due From, and Due To fields - they should show full text
        const fixTruncatedText = (element, maxWidth = null, charLimitOverride = null) => {
          const originalText = element.textContent || '';
          if (!originalText || originalText.trim() === '' || originalText.endsWith('...')) {
            return;
          }
          
          // Check if this is a Date Range, Due From, or Due To field - skip truncation
          const formControl = element.closest('.MuiFormControl-root');
          if (formControl) {
            const label = formControl.querySelector('.MuiInputLabel-root');
            if (label) {
              const labelText = label.textContent || '';
              if (labelText.includes('Date Range') || 
                  labelText.includes('Due From') || 
                  labelText.includes('Due To') ||
                  labelText.includes('reports.dateRange') ||
                  labelText.includes('reports.dueFrom') ||
                  labelText.includes('reports.dueTo')) {
                return;
              }
            }
          }
          
          const trimmedText = originalText.trim();
          let truncatedText = trimmedText;
          
          if (charLimitOverride && charLimitOverride > 0) {
            if (trimmedText.length > charLimitOverride) {
              truncatedText = trimmedText.substring(0, charLimitOverride) + '...';
            }
          } else if (maxWidth && maxWidth > 0) {
            const testSpan = clonedDoc.createElement('span');
            testSpan.style.visibility = 'hidden';
            testSpan.style.position = 'absolute';
            testSpan.style.whiteSpace = 'nowrap';
            testSpan.style.fontSize = window.getComputedStyle(element).fontSize;
            testSpan.style.fontFamily = window.getComputedStyle(element).fontFamily;
            testSpan.style.fontWeight = window.getComputedStyle(element).fontWeight;
            testSpan.style.padding = window.getComputedStyle(element).padding;
            clonedDoc.body.appendChild(testSpan);
            
            testSpan.textContent = trimmedText + '...';
            if (testSpan.offsetWidth > maxWidth) {
              // Binary search for the right length that fits
              let left = 0;
              let right = trimmedText.length;
              let bestLength = 0;
              
              while (left <= right) {
                const mid = Math.floor((left + right) / 2);
                testSpan.textContent = trimmedText.substring(0, mid) + '...';
                
                if (testSpan.offsetWidth <= maxWidth) {
                  bestLength = mid;
                  left = mid + 1;
                } else {
                  right = mid - 1;
                }
              }
              
              if (bestLength > 0 && bestLength < trimmedText.length) {
                truncatedText = trimmedText.substring(0, bestLength) + '...';
              }
            }
            
            clonedDoc.body.removeChild(testSpan);
          } else {
            // Fallback to character limit if no width provided
            const CHAR_LIMIT = CHARACTER_LIMITS.PIE_CHART_LABEL_TRUNCATE_LIMIT;
            if (trimmedText.length > CHAR_LIMIT) {
              truncatedText = trimmedText.substring(0, CHAR_LIMIT) + '...';
            }
          }
          
          // Update text if it was truncated
          if (truncatedText !== trimmedText) {
            element.textContent = truncatedText;
          }
        };
        
        // Fix chips with truncated text (only filter chips, not date fields)
        const chipLabels = clonedDoc.querySelectorAll('.reports-filter-chip .MuiChip-label');
        chipLabels.forEach(chipLabel => {
          const chipElement = chipLabel.closest('.MuiChip-root');
          if (chipElement) {
            const chipWidth = chipElement.clientWidth || chipElement.offsetWidth || 120;
            const labelPadding = parseInt(window.getComputedStyle(chipLabel).paddingLeft || '0', 10) + 
                                parseInt(window.getComputedStyle(chipLabel).paddingRight || '0', 10);
            const availableWidth = chipWidth - labelPadding - 20; // -20 for chip padding and margins
            fixTruncatedText(chipLabel, availableWidth);
          }
        });
        
        // Fix select values with truncated text (only for single-select fields without chips)
        // Don't modify select inputs that contain chips - those are handled above
        const selectInputs = clonedDoc.querySelectorAll('.MuiSelect-select, .MuiInputBase-input');
        selectInputs.forEach(input => {
          // Skip if this input contains chips (multi-select with chips)
          const hasChips = input.querySelector('.MuiChip-root, .reports-filter-chip-container');
          if (hasChips) {
            return; // Skip - chips are handled separately above
          }
          
          // Skip if this is inside a chip container
          const isInsideChip = input.closest('.reports-filter-chip-container, .MuiChip-root');
          if (isInsideChip) {
            return; // Skip - this is part of a chip
          }
          
          fixTruncatedText(input);
        });

        // Fix Sprint Management specific truncated texts
        const clonedMainContainer = clonedDoc.querySelector('.report-export-main-clone');
        
        if (clonedMainContainer) {
          const ticketTitles = clonedMainContainer.querySelectorAll('.ticket-title');
          ticketTitles.forEach(title => fixTruncatedText(title, null, 35));

          const sprintNames = clonedMainContainer.querySelectorAll('.sprint-name');
          sprintNames.forEach(name => {
            const containerWidth = name.clientWidth || 250;
            // UI allows 2 lines, use 1.6 to account for word-wrap jagged edges
            fixTruncatedText(name, containerWidth * 1.6);
          });
          
          const goalTexts = clonedMainContainer.querySelectorAll('.goal-text');
          goalTexts.forEach(goal => {
            const containerWidth = goal.clientWidth || 300;
            // UI allows 6 lines, use 4.5 instead of 5.8 to account for ragged word wrapping over multiple lines
            fixTruncatedText(goal, containerWidth * 4.5);
          });
        }
      },
    });

    addCanvasToPDF(canvas, pdf);
    
    const { startY, needsNewPage } = calculateTableStartY(canvas);
    let currentY = needsNewPage ? (pdf.addPage(), 20) : startY;

    for (let i = 0; i < tables.length; i++) {
      if (currentY > 250 && i > 0) {
        pdf.addPage();
        currentY = 20;
      }
      currentY = addTableToPDF(pdf, tables[i], currentY);
    }

    pdf.save(filename);
  } finally {
    removeExportContainer(exportContainer);
  }
};
